import axiosInstance from '../helpers/axios';
import { TOpsClassUpsert } from '../types';

// ── Comments (the per-record thread) ───────────────────────────────────────

export type TOpsPerson = {
  email: string;
  firstName: string | null;
  lastName: string | null;
  name: string;
};

export type TOpsComment = {
  id: string;
  recordId: string;
  recordObject: string;
  body: string;
  mentions: string[];
  actorEmail: string;
  actorName: string | null;
  at: string;
  editedAt: string | null;
};

export type TOpsRecordObject =
  | 'class'
  | 'account'
  | 'case'
  | 'contact'
  | 'program';

export const getOpsPeople = async () => {
  const response = await axiosInstance.get<TOpsPerson[]>('ops/comments/people');
  return response.data;
};

export const getOpsComments = async (recordId: string) => {
  const response = await axiosInstance.get<TOpsComment[]>('ops/comments', {
    params: { recordId },
  });
  return response.data;
};

export const getOpsCommentCounts = async (recordIds: string[]) => {
  const response = await axiosInstance.get<Record<string, number>>(
    'ops/comments/counts',
    { params: { recordIds: recordIds.join(',') } },
  );
  return response.data;
};

export const createOpsComment = async (dto: {
  recordId: string;
  recordObject: TOpsRecordObject;
  body: string;
}) => {
  const response = await axiosInstance.post<TOpsComment>('ops/comments', dto);
  return response.data;
};

export const editOpsComment = async (id: string, body: string) => {
  const response = await axiosInstance.patch<TOpsComment>(
    `ops/comments/${id}`,
    { body },
  );
  return response.data;
};

export const deleteOpsComment = async (id: string) => {
  const response = await axiosInstance.delete(`ops/comments/${id}`);
  return response.data;
};

// ── Notifications (the mention inbox) ──────────────────────────────────────

export type TOpsNotification = {
  id: string;
  fromEmail: string | null;
  fromName: string | null;
  recordId: string;
  recordObject: TOpsRecordObject;
  commentId: string | null;
  snippet: string;
  at: string;
  readAt: string | null;
};

export const getOpsNotifications = async () => {
  const response = await axiosInstance.get<{
    unread: number;
    items: TOpsNotification[];
  }>('ops/notifications');
  return response.data;
};

export const readOpsNotification = async (id: string) => {
  const response = await axiosInstance.post(`ops/notifications/${id}/read`);
  return response.data;
};

// ── Notes cascade ──────────────────────────────────────────────────────────

export type TOpsProgramNotes = {
  programId: string;
  programName: string | null;
  info: string | null;
  visible: boolean;
  via?: 'class' | 'opportunity';
};

export const getOpsClassProgramNotes = async (classId: string) => {
  const response = await axiosInstance.get<TOpsProgramNotes | null>(
    `ops/notes/class/${classId}`,
  );
  return response.data;
};

export const updateOpsProgramNotes = async (
  programId: string,
  dto: { info?: string; visible?: boolean },
) => {
  const response = await axiosInstance.patch<TOpsProgramNotes>(
    `ops/notes/program/${programId}`,
    dto,
  );
  return response.data;
};

// ── Series ─────────────────────────────────────────────────────────────────

export type TOpsSeriesSibling = {
  id: string;
  index: number;
  name: string | null;
  start: string | null;
  end: string | null;
  status: string | null;
  instructorName: string | null;
  localOrigin: boolean;
  current: boolean;
};

export type TOpsSeries = {
  seriesId: string;
  kind: 'portal' | 'mirror';
  count: number;
  items: TOpsSeriesSibling[];
};

export const getOpsClassSeries = async (classId: string) => {
  const response = await axiosInstance.get<TOpsSeries | null>(
    `ops/classes/${classId}/series`,
  );
  return response.data;
};

export const createOpsClassSeries = async (dto: {
  template: TOpsClassUpsert;
  dates: { start: string; end: string; name?: string }[];
}) => {
  const response = await axiosInstance.post<{
    seriesId: string;
    count: number;
    items: { id: string; index: number; name: string }[];
  }>('ops/classes/series', dto);
  return response.data;
};

export type TOpsSeriesRegisterResult = {
  seriesId: string;
  contactId: string | null;
  registered: number;
  refused: number;
  results: {
    classId: string;
    index: number | null;
    name: string | null;
    ok: boolean;
    registrationId?: string;
    status?: number;
    message?: string;
  }[];
};

export const registerOpsSeries = async (dto: {
  seriesId: string;
  contactId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  programId?: string;
}) => {
  const response = await axiosInstance.post<TOpsSeriesRegisterResult>(
    'ops/registrations/series',
    dto,
  );
  return response.data;
};

// ── Calendar file ──────────────────────────────────────────────────────────

export const downloadOpsClassIcs = async (classId: string) => {
  const response = await axiosInstance.get(`ops/classes/${classId}/ics`, {
    responseType: 'blob',
  });
  return response.data as Blob;
};

// ── Zoom room assignment ──────────────────────────────────────────────────

export type TZoomRoomOption = {
  id: string;
  name: string | null;
  number: number;
  url: string | null;
  roomCode: string | null;
  email: string | null;
  allowDouble: boolean;
  busy: {
    by: string;
    classId: string | null;
    start: string;
    end: string;
    source: 'reservation' | 'portal';
  } | null;
  current: boolean;
};

export type TZoomRoomOptions = {
  classId: string;
  window: { start: string; end: string; bufferMinutes: number };
  currentRoomId: string | null;
  free: number;
  items: TZoomRoomOption[];
};

export const getZoomRoomOptions = async (classId: string) =>
  (
    await axiosInstance.get<TZoomRoomOptions>(
      `ops/classes/${classId}/zoom/options`,
    )
  ).data;

export const assignZoomRoom = async (
  classId: string,
  roomId?: string,
  force?: boolean,
) =>
  (
    await axiosInstance.post<{
      classId: string;
      roomId: string;
      room: string | null;
      joinUrl: string | null;
      roomCode: string | null;
      forced: boolean;
    }>(`ops/classes/${classId}/zoom`, { roomId, force })
  ).data;

export const releaseZoomRoom = async (classId: string) =>
  (
    await axiosInstance.delete<{ classId: string; released: boolean }>(
      `ops/classes/${classId}/zoom`,
    )
  ).data;
